/**
 * /sla — a PAGE. AppLayoutRoute supplies the chrome.
 *
 * Every figure here must match the pricing page. An SLA that promises more than
 * the plan sells is worse than no SLA at all — so the tier names and the
 * commitments below are carried across verbatim, not paraphrased.
 *
 * Real content, not prompts, so the draft banner stays off.
 */
import { LegalPage, type LegalSection } from '../components/LegalPage';

const sections: LegalSection[] = [
  {
    heading: 'What this covers',
    body: (
      <>
        <p>
          This agreement applies to the infrastructure Bloomneo operates for you under an active
          subscription: servers, domains, DNS, certificates, backups, storage, transactional email
          and monitoring.
        </p>
        <p>
          It does not cover your own application code, or third-party services you run yourself. If
          your application has a bug, we will help you find it — but the uptime commitment below
          applies to the infrastructure beneath it.
        </p>
      </>
    ),
  },
  {
    heading: 'Uptime commitment',
    body: (
      <>
        <p>We commit to the following monthly uptime for the infrastructure we operate:</p>
        <ul>
          <li><strong>Basic</strong> — 99.5% monthly uptime</li>
          <li><strong>Standard</strong> — 99.9% monthly uptime</li>
          <li><strong>Enterprise</strong> — 99.95% monthly uptime</li>
        </ul>
        <p>
          For context, 99.9% allows roughly 43 minutes of unavailability in a 30-day month. We would
          rather state the real figure than round it up to a number that reads better.
        </p>
        <p>
          Uptime is measured by our monitoring against your production endpoints at one-minute
          intervals across a calendar month. A minute counts as down when the endpoint fails to
          respond correctly twice in succession.
        </p>
      </>
    ),
  },
  {
    heading: 'What is excluded',
    body: (
      <ul>
        <li>Scheduled maintenance announced at least 48 hours in advance</li>
        <li>Failures caused by your own code, configuration, or a third-party service you control</li>
        <li>Traffic beyond the capacity of your plan, where we have already advised an upgrade</li>
        <li>Upstream provider outages and force majeure events beyond our control</li>
        <li>Suspension for non-payment, following written notice</li>
      </ul>
    ),
  },
  {
    heading: 'Support response times',
    body: (
      <>
        <p>First response, measured from when your request reaches us:</p>
        <ul>
          <li><strong>Basic</strong> — business hours, Monday to Friday, 10am–7pm IST</li>
          <li><strong>Standard</strong> — within 4 hours during business hours</li>
          <li><strong>Enterprise</strong> — within 1 hour, with a named contact</li>
        </ul>
        <p>
          A critical incident — production unreachable, or data at risk — is treated as critical on
          every plan, including Basic. We do not leave an outage in a queue because of a tier.
        </p>
      </>
    ),
  },
  {
    heading: 'Severity levels',
    body: (
      <ul>
        <li>
          <strong>Critical.</strong> Production is down or unreachable, or data is at risk. We start
          immediately and keep you updated until service is restored.
        </li>
        <li>
          <strong>High.</strong> A significant component is failing but a workaround exists — mail
          not sending, or a certificate expiring within 48 hours.
        </li>
        <li>
          <strong>Normal.</strong> Something needs attention while the business keeps operating: a
          DNS change, a performance question, a scheduled restore.
        </li>
      </ul>
    ),
  },
  {
    heading: 'Backups and retention',
    body: (
      <>
        <p>Automated daily backups, retained according to plan:</p>
        <ul>
          <li><strong>Basic</strong> — 7 days</li>
          <li><strong>Standard</strong> — 30 days</li>
          <li><strong>Enterprise</strong> — 90 days, with a restore tested quarterly</li>
        </ul>
        <p>
          Backups are encrypted at rest on our infrastructure. A backup that has never been restored
          is an assumption rather than a safeguard, which is why restore testing is part of the
          Enterprise commitment and available on request for other plans.
        </p>
      </>
    ),
  },
  {
    heading: 'Certificates and renewals',
    body: (
      <p>
        We issue and renew TLS certificates for the domains we manage, and verify each renewal by
        reading the expiry date back from the server rather than assuming the renewal succeeded.
        Where a renewal fails, we act on it before the certificate expires.
      </p>
    ),
  },
  {
    heading: 'Scheduled maintenance',
    body: (
      <>
        <p>Where maintenance requires downtime, we will:</p>
        <ul>
          <li>Notify you at least 48 hours in advance</li>
          <li>Schedule it off-peak, typically between 2am and 6am IST</li>
          <li>Keep the window to the minimum needed</li>
          <li>Confirm when it is complete</li>
        </ul>
        <p>
          Emergency security patching may happen without notice. We will tell you afterwards, with
          the reason.
        </p>
      </>
    ),
  },
  {
    heading: 'If we miss these commitments',
    body: (
      <>
        <p>
          Where monthly uptime falls below the committed figure, you may claim a credit against the
          following month:
        </p>
        <ul>
          <li>Below the commitment but above 99.0% — 10% of that month's fee</li>
          <li>Between 95.0% and 99.0% — 25% of that month's fee</li>
          <li>Below 95.0% — 50% of that month's fee</li>
        </ul>
        <p>
          Claims should be made within 30 days of the affected month. Credits are the remedy under
          this agreement. If we miss the commitment in three consecutive months, you may terminate
          without notice and we will assist with migration at no charge.
        </p>
      </>
    ),
  },
  {
    heading: 'Incident reporting',
    body: (
      <p>
        After any critical incident we will tell you what happened, what caused it, and what we are
        changing so it does not recur — whether or not you ask, and whether or not the cause
        reflects well on us.
      </p>
    ),
  },
  {
    heading: 'Changes to this agreement',
    body: (
      <p>
        We may update this agreement. Where a change reduces a commitment, we will give 30 days'
        notice and you may terminate within that period without penalty.
      </p>
    ),
  },
];

export default function SlaPage() {
  return (
    <LegalPage
      draft={false}
      title="Service Level Agreement"
      description="Bloomneo's uptime commitments, support response times, backup retention and service credits, by plan."
      updated="16 August 2026"
      intro="Most service agreements are written to be unfalsifiable. This one states figures you can hold us to, and says plainly what is excluded."
      sections={sections}
    />
  );
}
